<script setup lang="ts">
import { ref } from 'vue'

import type { useSourceMedium } from '../../composables/useSourceMedium'
import type { DefinitionPlatformDescriptor } from '../../configs/platforms'

import { useI18n } from '@/shared/i18n'
import { Alert, Button, Modal, PlatformLogo, TagInput } from '@/shared/ui'

const props = defineProps<{
  platform: DefinitionPlatformDescriptor
  sourceMedium: ReturnType<typeof useSourceMedium>
}>()
const emit = defineEmits<{ close: [] }>()
const { t } = useI18n()
const open = ref(true)
const sources = ref([...(props.sourceMedium.sources.value.value ?? [])])
const mediums = ref([...(props.sourceMedium.mediums.value.value ?? [])])

async function save(): Promise<void> {
  const result = await props.sourceMedium.save(sources.value, mediums.value)
  if (result) emit('close')
}
</script>

<template>
  <Modal v-model="open" size="md" @close="emit('close')">
    <template #title>
      <span class="source-medium-modal__title flex items-center gap-sm">
        <PlatformLogo :platform="platform.channel" :size="20" />
        {{ t('workspaceDefinitions.sourceMedium.modalTitle') }}
      </span>
    </template>
    <template #body>
      <div class="source-medium-modal__body grid gap-md">
        <p>{{ t('workspaceDefinitions.sourceMedium.help', { platform: t(platform.labelKey) }) }}</p>
        <TagInput
          v-model="sources"
          :label="t('workspaceDefinitions.sourceMedium.sources')"
          :placeholder="t('workspaceDefinitions.sourceMedium.sourcePlaceholder')"
          :add-label="t('workspaceDefinitions.common.add')"
          :max="5"
          add-on-blur
        />
        <TagInput
          v-model="mediums"
          :label="t('workspaceDefinitions.sourceMedium.mediums')"
          :placeholder="t('workspaceDefinitions.sourceMedium.mediumPlaceholder')"
          :add-label="t('workspaceDefinitions.common.add')"
          :max="5"
          add-on-blur
        />
        <Alert v-if="sourceMedium.error.value" tone="danger">
          {{ t('workspaceDefinitions.common.saveError') }}
        </Alert>
      </div>
    </template>
    <template #buttons>
      <div class="source-medium-modal__actions flex items-center gap-sm justify-end">
        <Button
          variant="outline"
          size="sm"
          :disabled="sourceMedium.pending.value"
          @click="emit('close')"
        >
          {{ t('workspaceDefinitions.common.cancel') }}
        </Button>
        <Button size="sm" :loading="sourceMedium.pending.value" @click="save">
          {{ t('workspaceDefinitions.common.complete') }}
        </Button>
      </div>
    </template>
  </Modal>
</template>

<style scoped>
.source-medium-modal__body p {
  margin: 0;
  color: var(--ui-color-muted);
  font-size: 0.875rem;
}
</style>
